import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import FormatCurrency from 'components/Common/FormatCurrency'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { useIntl, FormattedMessage } from 'react-intl'

interface Props {
  variant: 'pastDates' | 'soldOut' | 'unavailable' | 'luxplus' | 'insurance' | undefined
  to?: string
  savings?: number
}

function CartReviewBanner(props: Props) {
  const {
    variant,
    to,
    savings,
  } = props

  const intl = useIntl()

  switch (variant) {
    case 'pastDates':
      return (
        <MessageBanner kind="warning">
          <FormattedMessage
            id="pG236lX0XddEbjAq3fcX"
            defaultMessage="Your selected dates are in the past {checkAvailabilityLink}"
            description="Warning banner when cart item dates are in the past"
            values={{
              checkAvailabilityLink: (
                <TextLink weight="bold" to={to} target="_blank">
                  {intl.formatMessage({
                    id: 'pWpFI_rOMPc59hwKkH9F',
                    defaultMessage: 'Check availability',
                    description: 'Hyperlink text to check availability on the cart review page',
                  })}
                </TextLink>
              ),
            }}
          />
        </MessageBanner>
      )
    case 'soldOut':
      return (
        <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
          <FormattedMessage
            id="mNvvj3NX2N9WzUI1DDfg"
            defaultMessage="Sold out for your dates {checkAvailabilityLink}"
            description="Warning banner when cart item is sold out for selected dates"
            values={{
              checkAvailabilityLink: (
                <TextLink weight="bold" to={to} target="_blank">
                  {intl.formatMessage({
                    id: 'pWpFI_rOMPc59hwKkH9F',
                    defaultMessage: 'Check availability',
                    description: 'Hyperlink text to check availability on the cart review page',
                  })}
                </TextLink>
              ),
            }}
          />
        </MessageBanner>
      )
    case 'unavailable':
      return (
        <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
          <FormattedMessage
            id="SqqmXFokyq3-MvSkG3hp"
            defaultMessage="Offer no longer available {viewSimilarLink}"
            description="Warning banner when cart item offer is no longer available"
            values={{
              viewSimilarLink: (
                <TextLink weight="bold" to={to} target="_blank">
                  {intl.formatMessage({
                    id: 'kSk7vAK6zWNZjMeEU25J',
                    defaultMessage: 'View similar offers',
                    description: 'Link text to view similar offers when item is unavailable',
                  })}
                </TextLink>
              ),
            }}
          />
        </MessageBanner>
      )
    case 'luxplus':
      if (savings) {
        return (
          <MessageBanner kind="promotional-primary" density="compact" icon={<LineInfoCircleIcon />}>
            <FormattedMessage
              id="wEtPTc9geh6shd6zX24C"
              defaultMessage="Add {programName} to save an extra {savingsAmount} on this booking & unlock member-only benefits all year round"
              description="Promotional banner showing LuxPlus savings for cart item"
              values={{
                programName: LUXURY_PLUS.PROGRAM_NAME,
                savingsAmount: <FormatCurrency value={savings} format="preciseDollar" />,
              }}
            />
          </MessageBanner>
        ) } else {
        return (
          <MessageBanner kind="promotional-primary">
            <FormattedMessage
              id="Oa1R5i49EijFUFiodcfO"
              defaultMessage="Save {discountAmount} on the joining fee when you book an eligible offer {searchOffersLink}"
              description="Promotional banner for LuxPlus joining fee discount"
              values={{
                discountAmount: <FormatCurrency value={500} />,
                searchOffersLink: (
                  <TextLink weight="bold" to={to}>
                    {intl.formatMessage({
                      id: 'z9jNlDywphqS-aKsbXN1',
                      defaultMessage: 'Search {programName} offers',
                      description: 'Link text to search LuxPlus offers in the search and discovery interface',
                    }, {
                      programName: LUXURY_PLUS.PROGRAM_NAME,
                    })}
                  </TextLink>
                ),
              }}
            />
          </MessageBanner>
        )
      }
    case 'insurance':
      return (
        <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
          {intl.formatMessage({
            id: 'BZO9q5DUJESItfxZ1QJG',
            defaultMessage: 'Please select an eligible product from your cart to proceed with Travel Protection.',
            description: 'Warning banner about selecting eligible product for travel protection insurance',
          })}
        </MessageBanner>
      )

    default:
      return null
  }
}

export default CartReviewBanner
